import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject, output } from "@angular/core";

import { AliasEditorComponent } from "../../../../components/common/alias-editor/alias-editor.component";
import { TrafficLineChart } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.component";
import { TrafficSubject } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.helpers";
import { SelfserviceToggle } from "../../../../components/selfservice/selfservice-toggle/selfservice-toggle";
import { L2vpnDetailContainer } from "../../../../components/subscription-detail/l2vpn-detail-container/l2vpn-detail-container";
import { Networkgraph } from "../../../../components/subscription-detail/networkgraph/networkgraph";
import { PageTabComponent } from "../../../../components/subscription-detail/page-tab/page-tab";
import { ServiceId } from "../../../../components/subscription-detail/service-id/service-id";
import { SubscriptionDetailHeader } from "../../../../components/subscription-detail/subscription-detail-header/subscription-detail-header";
import { ThirdPartyServiceIdsRowContentComponent } from "../../../../components/subscription-detail/third-party-service-ids-row-content/third-party-service-ids-row-content";
import { EnabledPipe } from "../../../../helpers/enabledpipe/enabledpipe";
import { SelfserviceDialogType } from "../../../../helpers/self-service/models/types";
import { ServiceSpeedPipe } from "../../../../helpers/service-speed-pipe/service-speed-pipe";
import { SubscriptionDatePipe } from "../../../../helpers/subscription-datepipe/subscription-datepipe";
import { AuthService } from "../../../../services/authservice";
import { L2vpnSubscriptionDetails } from "../../types";
import { SubscriptionViewBase } from "../subscription-view-base";
import { L2vpnShortCutMenu } from "./components/l2vpn-shortcut-menu/l2vpn-shortcut-menu";

@Component({
  selector: "l2vpn-subscription-view",
  templateUrl: "l2vpn-subscription-view.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AliasEditorComponent,
    EnabledPipe,
    L2vpnShortCutMenu,
    Networkgraph,
    PageTabComponent,
    SelfserviceToggle,
    ServiceId,
    ServiceSpeedPipe,
    SubscriptionDatePipe,
    SubscriptionDetailHeader,
    L2vpnDetailContainer,
    ThirdPartyServiceIdsRowContentComponent,
    TrafficLineChart,
    TranslatePipe,
  ],
})
export class L2vpnSubscriptionView extends SubscriptionViewBase<L2vpnSubscriptionDetails> {
  selfServiceOpen = output<SelfserviceDialogType>();

  private auth = inject(AuthService);

  protected speedPolicer = computed(() => this.subscription().speedPolicer);
  protected protection = computed(() => this.subscription().protection);
  protected topology = computed(() => this.subscription().topology);
  protected domain = computed(() => this.subscription().domain);
  protected serviceSpeed = computed(() => this.subscription().serviceSpeed);
  protected firewallEnabled = computed(() => this.subscription().firewallEnabled);

  protected isSelfServiceEnabled = computed(
    () =>
      !this.isDirty()
      && this.auth.isSelfServiceEnabled
      && this.auth.hasRole(["Infraverantwoordelijke"])
      && this.auth.isCurrentOrganisation(this.customerId()),
  );
  private translate = inject(TranslateService);
  protected chartSubjects = computed<TrafficSubject[]>(() =>
    buildL2vpnChartSubjects(this.subscription(), this.translate),
  );
}

export function buildL2vpnChartSubjects(
  subscription: L2vpnSubscriptionDetails,
  translate: TranslateService,
): TrafficSubject[] {
  const firewallInterconnectTitle = translate.instant("ServiceElement.FirewallInterconnect") as unknown as string;
  const portTitle = translate.instant("ServiceElement.Port") as unknown as string;

  const firewallItems = subscription.firewallInterconnects.map((fwInterconnect) => ({
    subscriptionId: subscription.subscriptionId,
    subscriptionInstanceIds: (fwInterconnect.endpoints || []).map((e) => e.subscriptionInstanceId),
    name: firewallInterconnectTitle,
  }));

  const portItems = subscription.esis
    .flatMap((esi) => esi?.endpoints || [])
    .filter((endpoint) => !!endpoint.port)
    .map((endpoint, index) => ({
      subscriptionId: subscription.subscriptionId,
      subscriptionInstanceIds: [endpoint.subscriptionInstanceId],
      name: `${portTitle} ${index + 1} - ${endpoint.port?.description}`,
    }));

  const allPortsItem = {
    subscriptionId: subscription.subscriptionId,
    subscriptionInstanceIds: [...firewallItems, ...portItems].flatMap((item) => item.subscriptionInstanceIds ?? []),
    name: translate.instant("Chart.AllPorts") as unknown as string,
  };

  return [allPortsItem, ...firewallItems, ...portItems];
}
